import { catColors } from "@/domain/categories";
import type { Category, RenderKeycap } from "@/domain/types";
import type { DiffPosition, DiffResult, DiffRow, DiffStatus } from "@/render/compare/diff";
import { ComboKbds } from "@/render/components/ComboKbds";
import { readoutLegend } from "@/render/readout/use-readout";

interface ChangesListProps {
  diff: DiffResult;
  categories: Record<string, Category>;
  /** The A-side keycaps, for resolving each A-position's legend through A's Keyboard Layout. */
  keycapsA: RenderKeycap[];
  /** The B-side keycaps, for resolving each B-position's legend through B's Keyboard Layout. */
  keycapsB: RenderKeycap[];
  /** The hovered row id (mirrors the diff flashlight); null when nothing is hovered. */
  hoverId: string | null;
  onHoverRow: (row: DiffRow) => void;
  onLeaveRow: () => void;
  onSelectRow: (row: DiffRow) => void;
}

const STATUS_CHIPS: DiffStatus[] = ["moved", "added", "removed"];

/** One side of a diff row: its position set as kbd pills, or "unbound" when empty. */
function DiffSide({ positions, keycaps }: { positions: DiffPosition[]; keycaps: RenderKeycap[] }) {
  if (positions.length === 0) return <span className="kl-diff-un">unbound</span>;
  return (
    <span className="kl-diff-side">
      {positions.map((p) => (
        <span key={`${p.code}@${p.combo}`} className="kl-list-keys">
          <ComboKbds combo={p.combo} />
          <kbd>{readoutLegend(p.code, keycaps)}</kbd>
        </span>
      ))}
    </span>
  );
}

/**
 * The Changes list: the compare-mode panel that replaces the Bindings list.
 * Summary chips plus one row per changed Command, each showing its position set on both
 * sides. Hovering a row drives the diff flashlight; clicking flashes and latches.
 */
export function ChangesList({
  diff,
  categories,
  keycapsA,
  keycapsB,
  hoverId,
  onHoverRow,
  onLeaveRow,
  onSelectRow,
}: ChangesListProps) {
  const counts: Record<DiffStatus, number> = { moved: 0, added: 0, removed: 0 };
  for (const r of diff.rows) counts[r.status]++;

  return (
    <aside className="kl-list">
      <div className="kl-list-card">
        <div className="kl-list-hd">
          <div className="kl-list-title">
            <span>Changes</span>
            <span className="kl-list-count">{diff.rows.length}</span>
          </div>
          <div className="kl-diff-sum">
            {STATUS_CHIPS.map((s) =>
              counts[s] ? (
                <span key={s} className={`kl-diff-chip ${s}`}>
                  {counts[s]} {s}
                </span>
              ) : null,
            )}
            <span className="kl-diff-chip same">{diff.same} same</span>
          </div>
        </div>
        <div className="kl-list-body">
          {diff.rows.length === 0 ? (
            <div className="kl-list-empty">
              No differences — these profiles map identically for the current selection.
            </div>
          ) : (
            <div className="kl-list-rows">
              {diff.rows.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  className={hoverId === r.id ? "kl-diff-row hot" : "kl-diff-row"}
                  onMouseEnter={() => onHoverRow(r)}
                  onMouseLeave={onLeaveRow}
                  onClick={() => onSelectRow(r)}
                >
                  <span className="kl-diff-top">
                    <span
                      className="kl-list-dot"
                      style={{ background: catColors(categories[r.category]).accent }}
                    />
                    <span className="kl-diff-name">{r.name}</span>
                    <span className={`kl-diff-badge ${r.status}`}>{r.status}</span>
                  </span>
                  <span className="kl-diff-keys">
                    <DiffSide positions={r.a} keycaps={keycapsA} />
                    <span className="kl-diff-arrow" aria-hidden="true">
                      →
                    </span>
                    <DiffSide positions={r.b} keycaps={keycapsB} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
